import { create } from 'zustand'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/shared/lib/supabase/client'

export type AuthStatus =
  | 'loading'
  | 'unauthenticated'
  | 'authenticated'
  | 'no-profile'
  | 'inactive'
  | 'wrong-role'

export interface ConnectorProfile {
  id: string
  fullName: string
  // The name as registered against the verified PAN/GST (from Cashfree/Income Tax), the
  // authoritative identity. Preferred over the self-typed fullName for display once verified.
  panRegisteredName: string | null
  firmPanRegisteredName: string | null
  gstinLegalName: string | null
  // The name to show: PAN/GST registered name when available, else the typed fullName.
  displayName: string
  phone: string | null
  connectorCode: string
  connectorType: string
  // The column is stored lowercase, so comparing against 'Firm' silently fails.
  // Derived once so no call site has to know that.
  isFirm: boolean
  isActive: boolean
  email: string | null
  emailVerified: boolean
  city: string | null
  state: string | null
  accountManagerName: string | null
  panVerified: boolean
  profilePictureUrl: string | null
  firmName: string | null
  firmType: string | null
  firmPanVerified: boolean
  gstin: string | null
  gstinVerified: boolean
  // Resolved from connector_bank_accounts, which the connectors row does not carry.
  bankVerified: boolean
  // A bank account was submitted and is valid, but its name did not match the PAN/GST
  // identity, so it is awaiting manual review by PSN staff rather than being usable yet.
  bankReviewPending: boolean
  whatsappSupportEnabled: boolean
  callSupportEnabled: boolean
}

interface AuthState {
  status: AuthStatus
  session: Session | null
  user: User | null
  profile: ConnectorProfile | null
  initialize: () => Promise<void>
  requestOtp: (phone: string) => Promise<{ error: string | null }>
  verifyOtp: (phone: string, token: string) => Promise<{ error: string | null }>
  logout: () => Promise<void>
  refreshProfile: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'loading',
  session: null,
  user: null,
  profile: null,

  initialize: async () => {
    const { data: { session } } = await supabase.auth.getSession()

    if (!session) {
      set({ status: 'unauthenticated', session: null, user: null, profile: null })
      return
    }

    set({ session, user: session.user })
    await loadProfile(session.user.id, set)

    supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (event === 'SIGNED_OUT' || !newSession) {
        set({ status: 'unauthenticated', session: null, user: null, profile: null })
        return
      }
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        set({ session: newSession, user: newSession.user })
        if (!get().profile) {
          await loadProfile(newSession.user.id, set)
        }
      }
    })
  },

  requestOtp: async (phone: string) => {
    const fullPhone = phone.startsWith('+91') ? phone : `+91${phone}`

    const { error } = await supabase.auth.signInWithOtp({
      phone: fullPhone,
    })

    if (error) {
      if (error.status === 429) {
        return { error: 'Too many attempts. Please wait before trying again.' }
      }
      return { error: 'Unable to send OTP. Please try again.' }
    }

    return { error: null }
  },

  verifyOtp: async (phone: string, token: string) => {
    const fullPhone = phone.startsWith('+91') ? phone : `+91${phone}`

    const { data, error } = await supabase.auth.verifyOtp({
      phone: fullPhone,
      token,
      type: 'sms',
    })

    if (error) {
      if (error.message?.includes('expired')) {
        return { error: 'OTP has expired. Please request a new one.' }
      }
      return { error: 'Invalid OTP. Please check and try again.' }
    }

    if (data.session) {
      set({ session: data.session, user: data.session.user })
      await loadProfile(data.session.user.id, set)
    }

    return { error: null }
  },

  logout: async () => {
    await supabase.auth.signOut()
    set({ status: 'unauthenticated', session: null, user: null, profile: null })
  },

  refreshProfile: async () => {
    const user = get().user
    if (user) {
      await loadProfile(user.id, set)
    }
  },
}))

type SetFn = (partial: Partial<AuthState>) => void

async function loadProfile(userId: string, set: SetFn) {
  const { data: connector, error } = await supabase
    .from('connectors')
    .select(
      'id, full_name, phone, connector_code, connector_type, is_active, email, email_verified, city, state, account_manager_name, pan_verified, pan_registered_name, firm_pan_registered_name, gstin_legal_name, profile_picture_url, firm_name, firm_type, firm_pan_verified, gstin, gstin_verified, whatsapp_support_enabled, call_support_enabled'
    )
    .eq('id', userId)
    .maybeSingle()

  if (error || !connector) {
    set({ status: 'no-profile', profile: null })
    return
  }

  // Bank verification lives on its own table, and the onboarding gate needs it, so it
  // is resolved here rather than by each screen that cares. A valid-but-name-mismatched
  // account sits in review_status='pending_review' and is neither verified nor a dead end.
  const { data: bankRows } = await supabase
    .from('connector_bank_accounts')
    .select('is_verified, review_status')
    .eq('connector_id', userId)
    .eq('is_active', true)

  const activeBanks = bankRows ?? []
  const bankVerified = activeBanks.some((b) => b.is_verified === true)
  const bankReviewPending = !bankVerified && activeBanks.some((b) => b.review_status === 'pending_review')

  if (!connector.is_active) {
    set({
      status: 'inactive',
      profile: mapProfile(connector, bankVerified, bankReviewPending),
    })
    return
  }

  set({
    status: 'authenticated',
    profile: mapProfile(connector, bankVerified, bankReviewPending),
  })
}

function mapProfile(
  row: {
    id: string
    full_name: string
    phone: string | null
    connector_code: string
    connector_type: string
    is_active: boolean
    email: string | null
    email_verified?: boolean | null
    city: string | null
    state: string | null
    account_manager_name: string | null
    pan_verified: boolean
    pan_registered_name?: string | null
    firm_pan_registered_name?: string | null
    gstin_legal_name?: string | null
    profile_picture_url: string | null
    whatsapp_support_enabled?: boolean | null
    call_support_enabled?: boolean | null
    firm_name: string | null
    firm_type: string | null
    firm_pan_verified: boolean
    gstin: string | null
    gstin_verified: boolean
  },
  bankVerified: boolean,
  bankReviewPending: boolean
): ConnectorProfile {
  const isFirm = (row.connector_type ?? '').toLowerCase() === 'firm'
  const panRegisteredName = row.pan_registered_name ?? null
  const firmPanRegisteredName = row.firm_pan_registered_name ?? null
  const gstinLegalName = row.gstin_legal_name ?? null
  // The verified, authoritative name wins over the self-typed one. For a firm the company
  // PAN name is the identity; for an individual it is the personal PAN name.
  const displayName =
    (isFirm ? firmPanRegisteredName || gstinLegalName : panRegisteredName) || row.full_name

  return {
    id: row.id,
    fullName: row.full_name,
    panRegisteredName,
    firmPanRegisteredName,
    gstinLegalName,
    displayName,
    phone: row.phone,
    connectorCode: row.connector_code,
    connectorType: row.connector_type,
    // The column is stored lowercase, so every caller comparing against 'Firm'
    // silently failed. Derive it once here instead of comparing strings at each site.
    isFirm,
    isActive: row.is_active,
    email: row.email,
    emailVerified: row.email_verified ?? false,
    city: row.city,
    state: row.state,
    accountManagerName: row.account_manager_name,
    panVerified: row.pan_verified,
    profilePictureUrl: row.profile_picture_url,
    whatsappSupportEnabled: row.whatsapp_support_enabled ?? true,
    callSupportEnabled: row.call_support_enabled ?? true,
    firmName: row.firm_name,
    firmType: row.firm_type,
    firmPanVerified: row.firm_pan_verified,
    gstin: row.gstin,
    gstinVerified: row.gstin_verified,
    bankVerified,
    bankReviewPending,
  }
}
